import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useChatStore } from '../../../store/chatStore';
import { layerConfirm, layerAlert } from '../../../components/LayerPopup/LayerPopup';
import { useAuthStore } from '../../../store/authStore';
// chat-room-name / chat-display-names — 방을 무엇이라 부를지 한 곳에서 정한다.
import { resolveRoomName } from '../../../lib/chat-room-name';
import { chatPreview } from '../../../lib/chat-preview';
import { useChatDisplayNameStore, myNameFor } from '../../../store/chatDisplayNameStore';
import type { ChatRoomDto, ChatFilter } from '../../../types/chat';
// ⚠ `Users` 는 2026-09-23 에 빠졌다 — 줄의 인원수 자리를 마지막 메시지가 가져갔다.
import { Bot, Edit, LogOut, MoreVertical, Search } from '../../../icons';
import { Avatar } from '../../../components/shared/Avatar';
import { AiAvatar } from '../../../components/AiAvatar';
import { NewChatModal } from './NewChatModal';
import { leaveRoom as leaveRoomApi } from '../../../api/chat';
import {
  tChat, getChatLang, subscribeChatLang,
  type ChatI18nKey,
} from '../../../lib/chat-i18n';

interface ChatListProps {
  rooms: ChatRoomDto[];
  loading: boolean;
  error: string | null;
  /** 새 채팅 모달이 방을 생성/재사용했을 때 — ChatPage 가 rooms 갱신 + 활성화. */
  onCreateRoom: (room: ChatRoomDto) => void;
  /** 사용자가 방을 나갔을 때 — ChatPage 가 rooms 목록 갱신 + 활성 정리. */
  onLeaveRoom: (roomId: string) => void;
}

const FILTER_OPTIONS: ReadonlyArray<{ id: ChatFilter; labelKey: ChatI18nKey }> = [
  { id: 'all', labelKey: 'chat.sidebar.filter.all' },
  { id: 'direct', labelKey: 'chat.sidebar.filter.direct' },
  { id: 'group', labelKey: 'chat.sidebar.filter.group' },
  { id: 'team', labelKey: 'chat.sidebar.filter.team' },
];

/**
 * ChatList (P2-16a) — 시안 line 1063~1123 의 좌측 패널.
 *
 * 책임:
 *   - 본인이 멤버인 room 목록 표시 (props.rooms)
 *   - 검색·필터 (chatStore SSOT)
 *   - 활성 room 시각 강조 (border-left + tinted bg)
 *   - 클릭 시 setActiveChat
 *
 * P2-16 코어: avatar 는 type 별 색상 + initials/icon. 시안 status dot (online/busy) 는
 * room 응답에 status 가 없으므로 후속(member presence) 으로 deferred.
 */
export function ChatList({ rooms, loading, error, onCreateRoom, onLeaveRoom }: ChatListProps) {
  useSyncExternalStore(subscribeChatLang, getChatLang, getChatLang);
  const activeChatId = useChatStore((s) => s.activeChatId);
  const setActiveChat = useChatStore((s) => s.setActiveChat);
  const search = useChatStore((s) => s.chatSearch);
  const setSearch = useChatStore((s) => s.setSearch);
  const filter = useChatStore((s) => s.chatFilter);
  const setFilter = useChatStore((s) => s.setFilter);
  const [showNewChat, setShowNewChat] = useState(false);

  const filteredRooms = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rooms.filter((r) => {
      if (filter !== 'all' && r.type !== filter) return false;
      if (!q) return true;
      const name = (r.name ?? '').toLowerCase();
      return name.includes(q);
    });
  }, [rooms, filter, search]);

  return (
    <aside className="chat-list" aria-label={tChat('chat.sidebar.aria')}>
      <div className="chat-list-header">
        <div className="chat-list-title-row">
          <h2 className="chat-list-title">{tChat('chat.sidebar.title')}</h2>
          <button
            type="button"
            className="chat-list-new-button"
            onClick={() => setShowNewChat(true)}
            aria-label={tChat('chat.sidebar.aria_new')}
          >
            <Edit size={16} />
          </button>
        </div>
        <div className="chat-list-search">
          <Search className="chat-list-search-icon" size={16} />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tChat('chat.sidebar.search_placeholder')}
            className="chat-list-search-input"
            aria-label={tChat('chat.sidebar.aria_search')}
          />
        </div>
        <div className="chat-list-filters" role="tablist" aria-label={tChat('chat.sidebar.aria_filter_tablist')}>
          {FILTER_OPTIONS.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-pressed={filter === f.id}
              onClick={() => setFilter(f.id)}
              className="chat-list-filter-button"
            >
              {tChat(f.labelKey)}
            </button>
          ))}
        </div>
      </div>

      <div className="chat-list-rooms" role="listbox" aria-label={tChat('chat.sidebar.aria_rooms_listbox')}>
        {loading && rooms.length === 0 && <div className="chat-list-loading">{tChat('chat.sidebar.loading')}</div>}
        {error && rooms.length === 0 && <div className="chat-list-error">{tChat('chat.sidebar.err_load', { msg: error })}</div>}
        {!loading && !error && filteredRooms.length === 0 && (
          <div className="chat-list-empty">{tChat('chat.sidebar.empty')}</div>
        )}
        {filteredRooms.map((room) => (
          <RoomItem
            key={room.id}
            room={room}
            active={activeChatId === room.id}
            onSelect={() => setActiveChat(room.id)}
            onLeave={() => onLeaveRoom(room.id)}
          />
        ))}
      </div>

      {showNewChat && (
        <NewChatModal
          onClose={() => setShowNewChat(false)}
          onCreated={(room) => {
            setShowNewChat(false);
            onCreateRoom(room);
          }}
        />
      )}
    </aside>
  );
}

interface RoomItemProps {
  room: ChatRoomDto;
  active: boolean;
  onSelect: () => void;
  onLeave: () => void;
}

function RoomItem({ room, active, onSelect, onLeave }: RoomItemProps) {
  const time = formatRoomTime(room.lastMessageAt);
  // 방별 미읽음은 chatStore SSOT 구독 (실시간 bump/clear 반영). 초기값은 room.unreadCount fallback.
  const storeUnread = useChatStore((s) => s.roomUnread[room.id]);
  const unread = storeUnread ?? room.unreadCount ?? 0;
  // 멤버 총원: 목록 응답(room.memberCount) 우선, 없으면 store(활성 방은 getRoom 후 set) fallback.
  const storeMemberCount = useChatStore((s) => s.roomMemberCountByRoom[room.id]);
  const memberCount = room.memberCount ?? storeMemberCount ?? null;
  // 본인 제외 멤버 미리보기 — 직원 대화 아바타용. memberPreview 는 목록 응답 한정(최대 4명).
  const myId = useAuthStore((s) => s.user?.id?.toString() ?? '');
  const preview = room.memberPreview ?? [];
  const others = preview.filter((m) => m.userId !== myId);
  // AI 전용 방 = 본인만 멤버(memberCount===1). NewChatModal·MessageInput 과 동일 신호.
  const isAiRoom = memberCount === 1;
  // group/team 겹침 스택: 본인 제외 우선, 없으면 전체. 최대 3명 + 초과 시 +N.
  const stackList = (others.length > 0 ? others : preview).slice(0, 3);
  const nonSelfTotal = memberCount != null ? Math.max(memberCount - 1, others.length) : others.length;
  const stackExtra = Math.max(nonSelfTotal - stackList.length, 0);
  // direct(1:1) 단일 아바타 대상 — 상대 1명(없으면 첫 멤버 fallback).
  const directMember = others[0] ?? preview[0] ?? null;
  // 마지막 메시지 미리보기 — 글자 판정은 `lib/chat-preview` 한 곳(첫 화면 위젯과 같은 것).
  const lastMsg = chatPreview(room.lastMessagePreview, { myUserId: myId, isDirect: room.type === 'direct' });
  // 내가 부르는 이름 → 공용 이름 → 기본(1:1 은 상대 이름 · 그 밖은 단체대화방).
  const myRoomName = useChatDisplayNameStore((st) => myNameFor(st.names, 'room', room.id));
  const shownRoomName = resolveRoomName({
    type: room.type, name: room.name, members: preview, myId, myName: myRoomName,
    fallback: tChat('chat.sidebar.room.fallback_name'),
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function onDoc(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    window.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const handleLeave = async () => {
    setMenuOpen(false);
    if (leaving) return;
    /* ★ 봇방은 「나가기」가 곧 「자동화 삭제」다 — 같은 문구를 쓰면 사용자가 모르고 지운다. */
    const ok = await layerConfirm(tChat(
      room.type === 'ai' ? 'chat.sidebar.room.leave_confirm_bot' : 'chat.sidebar.room.leave_confirm',
    ));
    if (!ok) return;
    setLeaving(true);
    try {
      await leaveRoomApi(room.id);
      onLeave();
    } catch (e) {
      void layerAlert(tChat('chat.sidebar.room.leave_failed', { msg: e instanceof Error ? e.message : String(e) }));
    } finally {
      setLeaving(false);
    }
  };

  return (
    <div
      role="option"
      aria-selected={active}
      onClick={onSelect}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && (e.target as HTMLElement).closest('.chat-list-room-menu') === null) {
          e.preventDefault();
          onSelect();
        }
      }}
      tabIndex={0}
      className={`chat-list-room${active ? ' chat-list-room--active' : ''}`}
    >
      {isAiRoom ? (
        // AI 와의 대화 — AI 아이콘(그라디언트).
        <div className="chat-list-room-avatar chat-list-room-avatar--ai" aria-label={tChat('chat.item.ai_assistant')}>
          <AiAvatar />
        </div>
      ) : room.type === 'direct' ? (
        // 1:1 — 상대 1명 단일 프로필.
        <div className="chat-list-room-avatar chat-list-room-avatar--photo">
          <Avatar
            userId={Number(directMember?.userId ?? room.id)}
            name={directMember?.name ?? shownRoomName}
            src={directMember?.avatarUrl ?? null}
            size="md"
          />
        </div>
      ) : (
        // group/team — 멤버 프로필 겹침 스택 + 초과 인원 +N.
        <div className="chat-list-room-avatar-stack" aria-label={tChat('chat.sidebar.room.member_count', { n: nonSelfTotal })}>
          {stackList.map((m) => (
            <span className="chat-list-room-avatar-stack-item" key={m.userId}>
              <Avatar userId={Number(m.userId)} name={m.name} src={m.avatarUrl} size="xs" />
            </span>
          ))}
          {stackExtra > 0 && <span className="chat-list-room-avatar-stack-more">+{stackExtra}</span>}
        </div>
      )}
      <div className="chat-list-room-meta">
        <div className="chat-list-room-meta-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <span className="chat-list-room-name">{shownRoomName}</span>
            {isAiRoom && <Bot size={11} style={{ color: 'var(--primary)', flexShrink: 0 }} aria-hidden />}
          </div>
          {time && <span className="chat-list-room-time">{time}</span>}
        </div>
        <div className="chat-list-room-preview-row">
          {/*
            마지막 메시지 미리보기 (2026-09-23 사용자 결정).
            ★ 이 칸은 원래 **비어 있었다** — 자리를 새로 만든 것이 아니라 비워 둔 칸을 채운다.
              그래서 줄 높이가 안 바뀐다.
            ⚠ **고른 방에 있던 「인원수」를 «뺀다»** — 같은 자리를 둘이 쓸 수 없고, 사용자가
              그것을 «보고» 마지막 메시지를 골랐다. 인원수는 방 상세에서 본다.
            ★ 무슨 글자를 보일지는 `lib/chat-preview` 한 곳이 정한다 — 첫 화면 「최근 대화」
              위젯과 **같은 판정**이라, 같은 방이 두 화면에서 다른 말을 하지 않는다.
          */}
          <span className={`chat-list-room-preview chat-list-room-preview--${lastMsg.kind}`}>
            {lastMsg.text}
          </span>
          {unread > 0 && <div className="chat-list-room-unread">{unread > 99 ? '99+' : unread}</div>}
        </div>
      </div>
      <div className="chat-list-room-menu" ref={menuRef} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="chat-list-room-menu-trigger"
          onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label={tChat('chat.sidebar.room.menu_aria')}
          disabled={leaving}
        >
          <MoreVertical size={14} />
        </button>
        {menuOpen && (
          <div className="chat-list-room-menu-popover" role="menu">
            <button
              type="button"
              role="menuitem"
              className="chat-list-room-menu-item chat-list-room-menu-item--danger"
              onClick={handleLeave}
              disabled={leaving}
            >
              <LogOut size={13} />
              <span>{leaving ? tChat('chat.sidebar.room.leaving') : tChat('chat.sidebar.room.leave')}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function formatRoomTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  }
  return `${d.getMonth() + 1}/${d.getDate()}`;
}
